import axios from 'axios';
import { useEffect, useRef } from 'react';
import { Form, redirect, useLoaderData } from 'react-router-dom';
import { useApplicationStore } from '../../stores/applicationStore';
import { useUserStore } from '../../stores/userStore';

export async function loader() {
	const { isSignedIn } = useUserStore.getState();
	if (!isSignedIn()) {
		return redirect('/login');
	}
	// load hub info for consent text
	const { program } = useApplicationStore.getState();
	if (!program) {
		return redirect('/home');
	} else {
		const entity = await axios.get(`/clients/get-hub`, {
			params: {
				hubUrl: program.tags_organization
					?.find(({ category }) => category === 'participating_hub')
					?.traits?.find(({ name }) => name === 'participating_hub_url')?.value,
			},
		});
		return entity.data;
	}
}

export async function action() {
	const { clearApplication, getApplication } = useApplicationStore.getState();
	const { user } = useUserStore.getState();
	try {
		await axios.post(
			`/clients/${user.id}/submit-application`,
			getApplication(),
		);
		clearApplication();
		return redirect('/home');
	} catch (err) {
		console.error('Error submitting service request', err);
		return null;
	}
}

const SubmitRequest = () => {
	const entity = useLoaderData();
	const { program, setIdempotencyKey } = useApplicationStore();
	// Stable per submission attempt: generated once when this step mounts so
	// error-retries of the same application reuse it (letting the backend dedupe),
	// while a fresh application gets a new key. The action reads it back off the
	// store via getApplication(), so push it there on mount.
	const idempotencyKey = useRef(crypto.randomUUID());
	useEffect(() => {
		setIdempotencyKey(idempotencyKey.current);
	}, [setIdempotencyKey]);
	// The loader guarantees a program on entry, but a successful submit clears it
	// and redirects — guard the brief re-render in that window so the unguarded
	// program.locations reads below can't throw.
	if (!program) {
		return null;
	}
	const taxonomyName = program.name;
	const hubName = program?.tags_organization?.find(
		({ category }) => category === 'participating_hub',
	)?.subcategory_display;
	return (
		<div className="page submit-request">
			<div className="sm:mx-auto sm:w-full sm:max-w-2xl">
				Submit Service Request
			</div>
			<div className="sm:mx-auto sm:w-full sm:max-w-2xl">
				<Form method="post">
					<h3>{taxonomyName}</h3>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<div>{program?.locations?.[0]?.organization_name}</div>
					</div>
					<div className="mb-4 text-sm">
						<strong>Our purpose:</strong> The <strong>{hubName}</strong> was
						created to support connections to social need services in a manner
						that: (1) reduces barriers to accessing services, (2) protects
						privacy of personal information, and (3) strengthen the{' '}
						<strong>{hubName}</strong> social care safety net.
						<br />
						<strong>Our commitment to you:</strong> We are committed to ensuring
						that you control your personal data. You own all data in your
						profile and can modify or delete your profile at any time.
						Identifiable data is only shared upon your request when you apply
						for services. After you apply for a service, the only information
						shared with the referring agency is the status and time of status
						changes your request.
						<br />
						<strong>Consent to share data:</strong> Today, you are applying for{' '}
						<strong>{taxonomyName}</strong> from{' '}
						<strong>{program.locations[0].organization_name}</strong>, which
						requires sharing of the following data:
						<ul className="ml-6 list-disc">
							<li className="my-2">
								To <strong>{program.locations[0].organization_name}</strong>,
								for the purposes of determining your eligibility for the
								service: Your contact information, account ID, name of referring
								agency if applicable, application information necessary to
								determine eligibility, which may include the name of any
								service-related funding source.
							</li>
							<li className="my-2">
								To <strong>{entity.entity_name}</strong> for the purposes of CIE
								intermediary administration, providing additional support if
								needed, and strengthening of the social care safety-net: First
								and last name, date of birth, your phone number, your account
								ID, service agency name, referring agency name, service request,
								service status changes and dates.
							</li>
						</ul>
						I hereby request and authorize the exchange of data, as described
						above, for the purposes described. In doing so, I am aware that I
						may, without penalty, refuse consent for the exchange of information
						by any agencies listed. I understand that I can seek additional
						information about my data, how it is protected, and how it is shared
						at any time by contacting{' '}
						<strong>
							<a
								target="_blank"
								href={entity.hub_org_url}
								rel="noreferrer"
								className="link"
							>
								{entity.hub_org_url}
							</a>
						</strong>
						.
					</div>
					<p>
						Please confirm you would like to submit a request for the service
						above.
					</p>
					<div className="mt-6">
						<button type="submit" className="btn btn-primary btn-block">
							Submit Request
						</button>
					</div>
				</Form>
			</div>
		</div>
	);
};

export default SubmitRequest;
